import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { toast } from "sonner";
import { AuthLayout } from "@/components/auth/AuthLayout";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { claimInvite } from "@/lib/invites.functions";
import { resolveLandingRoute } from "@/lib/routeAfterLogin";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "Set New Password — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: "/reset-password" }],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingLink, setCheckingLink] = useState(true);
  const [linkError, setLinkError] = useState<string | null>(null);
  const claim = useServerFn(claimInvite);

  useEffect(() => {
    let active = true;

    async function preparePasswordSession() {
      const current = await supabase.auth.getSession();
      if (current.data.session) {
        if (active) setCheckingLink(false);
        return;
      }

      const code = new URL(window.location.href).searchParams.get("code");
      if (code) {
        const { error } = await supabase.auth.exchangeCodeForSession(code);
        if (error && active) {
          setLinkError("This secure setup link is expired. Request a fresh password setup link.");
          setCheckingLink(false);
          return;
        }
      }

      const next = await supabase.auth.getSession();
      if (active) {
        if (!next.data.session) {
          setLinkError("Open the secure link from your email first, or request a fresh password setup link.");
        }
        setCheckingLink(false);
      }
    }

    void preparePasswordSession();
    return () => {
      active = false;
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    const { data, error } = await supabase.auth.updateUser({ password });
    if (error) {
      setLoading(false);
      toast.error(error.message);
      return;
    }
    // If this user was invited, claim the invite and route into their portal.
    let claimed: { intended_role?: string } | null = null;
    try {
      claimed = await claim();
    } catch {
      // Non-fatal: password is set, they can sign in normally.
    }
    setLoading(false);
    toast.success("Password set. Welcome.");
    const uid = data.user?.id;
    if (uid) {
      const dest = claimed?.intended_role ? await resolveLandingRoute(uid) : await resolveLandingRoute(uid);
      window.location.assign(dest);
      return;
    }
    navigate({ to: "/login" });
  };

  return (
    <AuthLayout eyebrow="Secure Setup" heading="Create your portal password">
      <form onSubmit={onSubmit} className="space-y-5">
        {checkingLink ? (
          <p className="font-sans text-[14px] leading-relaxed text-charcoal-500">
            Verifying your secure setup link…
          </p>
        ) : linkError ? (
          <div className="border border-bronze-600/30 bg-cream-50 p-4 font-sans text-[13px] leading-relaxed text-charcoal-500">
            <p>{linkError}</p>
            <Link to="/forgot-password" className="mt-3 inline-block font-semibold text-bronze-600 hover:text-bronze-400">
              Send a fresh setup link
            </Link>
          </div>
        ) : (
          <p className="font-sans text-[14px] leading-relaxed text-charcoal-500">
            Set a password for your Currency Thoughts portal. You will be taken into the correct console after this step.
          </p>
        )}
        <PwdField id="password" label="New Password" value={password} onChange={setPassword} />
        <PwdField id="confirm" label="Confirm Password" value={confirm} onChange={setConfirm} />
        <PrimaryButton type="submit" size="lg" disabled={loading || checkingLink || Boolean(linkError)} className="w-full">
          {loading ? "Updating…" : "Update Password"}
        </PrimaryButton>
      </form>
    </AuthLayout>
  );
}

function PwdField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-charcoal-500"
      >
        {label}
      </label>
      <input
        id={id}
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        autoComplete="new-password"
        className="mt-2 w-full border border-bronze-600/40 bg-cream-50 px-4 py-3 font-sans text-[14px] text-charcoal-900 focus:border-bronze-600 focus:outline-none"
        style={{ borderRadius: "var(--radius-button)" }}
      />
    </div>
  );
}